import { path, reverse, sortBy, prop, isNil, pipe } from 'ramda';

import SortingTypes from 'constants/SortingTypes';
import SortingColumns from 'constants/SortingColumns';

// kw-gap virtual-table column ids → this service's attribute vocabulary
// so sort routes to the right branch instead of the nil-prop fallback.
const ATTRIBUTE_ALIASES = {
    avgSearchVolume: SortingColumns.SEARCH_COUNT,
    keywordDifficulty: SortingColumns.RANK,
};

/**
 * Sorting service for result list data.
 */
class ResultSorterService {
    /**
     * Main sort function.
     *
     * @param {data: Array} Array of data.
     * @param {settings: Object} Sorting column and type.
     * @return {Array} Sorted data.
     */
    static sort({ data, settings, selectedSearchVolumeTimeframe }) {
        const attribute = ATTRIBUTE_ALIASES[settings.column] ?? settings.column;
        const reversed = this.getReversedFlag(settings.type, attribute);

        if (data.length === 0) {
            return data;
        } else if (isNil(attribute) || attribute === SortingColumns.RELEVANCY) {
            if (reversed === false) {
                return data;
            } else {
                return reverse(data);
            }
        } else if (attribute === SortingColumns.SEARCH_COUNT) {
            if (reversed === true) {
                return sortBy(pipe(path(['searchVolumesByTimeframe', selectedSearchVolumeTimeframe]), val => -val))(
                    data,
                );
            } else {
                return sortBy(path(['searchVolumesByTimeframe', selectedSearchVolumeTimeframe]))(data);
            }
        } else {
            // Custom sorting function that handles null values properly
            // Null values should always appear last regardless of sort direction
            // Secondary sort by keyword for consistent ordering
            // Replace sortBy with custom sort to handle mixed number/string types properly
            const sorted = [...data].sort((a, b) => {
                const rawA = prop(attribute)(a);
                const rawB = prop(attribute)(b);
                const keywordA = (prop('keyword')(a) || prop('kw')(a) || '').toLowerCase();
                const keywordB = (prop('keyword')(b) || prop('kw')(b) || '').toLowerCase();

                // Handle nulls - always at end
                const nullA = isNil(rawA) || rawA === '-' || rawA === '';
                const nullB = isNil(rawB) || rawB === '-' || rawB === '';
                if (nullA && nullB) {
                    return keywordA.localeCompare(keywordB);
                }
                if (nullA) {
                    return 1;
                }
                if (nullB) {
                    return -1;
                }

                // Try numeric conversion
                const numA = Number(rawA);
                const numB = Number(rawB);
                const valA = !isNaN(numA) ? numA : rawA;
                const valB = !isNaN(numB) ? numB : rawB;

                // Primary sort
                if (valA < valB) {
                    return -1;
                }
                if (valA > valB) {
                    return 1;
                }

                // Secondary sort by keyword
                return keywordA.localeCompare(keywordB);
            });

            if (reversed === true) {
                return reverse(sorted);
            } else {
                return sorted;
            }
        }
    }

    static getReversedFlag(type, attribute) {
        const reversed = type === SortingTypes.DESC;
        // Flip reversed when we deal with RELEVANCY.
        return attribute === SortingColumns.RELEVANCY ? !reversed : reversed;
    }
}

export default ResultSorterService;
